import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { DietaryRow } from '@fusion/dietary/row'
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material'

import React, { useState } from 'react'

import ItemDetail from './shared/ItemDetail'

const canteenOrderedItemDetailItemDataFragment = graphql(`
  fragment CanteenOrderedItemDetail_ItemDataFragment on CCOrderItem {
    id
    name
    qty
    canCancel
    description
    dietaries
    allergens
    subAllergens {
      cereals
      nuts
    }
    liveMenuItem {
      _id
      coreLiveItem {
        isCombo
        image
      }
    }
    vendor {
      logo
      companyName
      id
    }
    options {
      items {
        id
        name
        position
        price
        category
        qty
        dietaries
        allergens
        subAllergens {
          cereals
          nuts
        }
      }
    }
  }
`)

type Props = {
  itemData: FragmentType<typeof canteenOrderedItemDetailItemDataFragment>
  cancelItem: (ccOrderItemId: number) => void
}

const OrderedItemDetail: React.FC<Props> = ({ itemData: itemDataProp, cancelItem }) => {
  const {
    id,
    name,
    liveMenuItem,
    vendor,
    description,
    dietaries,
    allergens,
    subAllergens,
    options,
    canCancel,
  } = useFragment(canteenOrderedItemDetailItemDataFragment, itemDataProp)
  const [awaitingDeleteConfirmation, setAwaitingDeleteConfirmation] = useState(false)

  const handleDeleteIntention = () => {
    setAwaitingDeleteConfirmation(true)
  }

  const handleCancelDeleteIntention = () => {
    setAwaitingDeleteConfirmation(false)
  }

  const handleDeleteConfirmation = () => {
    cancelItem(id)
  }

  return (
    <ItemDetail
      itemName={name}
      vendorName={vendor.companyName}
      vendorLogo={vendor.logo || ''}
      itemImage={liveMenuItem.coreLiveItem?.image || ''}>
      <CardHeader title={name} />
      <CardContent sx={{ height: '100%' }}>
        <Stack spacing={4} direction="column" width="100%">
          <Typography variant="body1">{description}</Typography>
          <DietaryRow
            isComboItem={Boolean(liveMenuItem.coreLiveItem?.isCombo)}
            dietaries={dietaries || []}
            allergens={allergens || []}
            subAllergens={subAllergens}
          />
          <Stack direction="column">
            {options.items.map((item) => (
              <Stack spacing={1} alignItems="center" key={item.id} direction="row">
                <Checkbox checked disabled />
                <Typography>
                  {item.qty > 1 ? `${item.qty} ` : null}
                  {item.name}
                </Typography>
                <DietaryRow
                  dietaries={item?.dietaries || []}
                  allergens={item?.allergens || []}
                  subAllergens={item?.subAllergens}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </CardContent>
      <CardActions
        sx={{
          position: { xs: 'fixed', md: 'unset' },
          bottom: { xs: 0, md: 'unset' },
          width: { xs: '100%', md: 'unset' },
        }}>
        {!awaitingDeleteConfirmation ? (
          <Button
            fullWidth
            disabled={!canCancel}
            onClick={() => handleDeleteIntention()}
            variant="contained"
            color="error">
            Cancel item
          </Button>
        ) : (
          <Stack direction="column" spacing={1} width="100%">
            <Typography>Are you sure you want to cancel {name}?</Typography>
            <Stack direction="row" spacing={1} width="100%">
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={() => handleDeleteConfirmation()}>
                Yes, cancel it
              </Button>
              <Button fullWidth variant="text" onClick={() => handleCancelDeleteIntention()}>
                No, keep it
              </Button>
            </Stack>
          </Stack>
        )}
      </CardActions>
    </ItemDetail>
  )
}

export default OrderedItemDetail
